import { db } from '../firebase-config';
import { parseISODate, getMonday, getSunday, formatDateYYYYMMDD } from '../utils/dateUtils';
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  doc,
  Timestamp,
  serverTimestamp,
  onSnapshot,
  getDoc,
  orderBy,
  limit,
  setDoc
} from 'firebase/firestore';

class WorkSessionService {
  // Collection references
  constructor() {
    this.workSessionsCollection = collection(db, 'workSessions');
    this.breaksCollection = collection(db, 'breaks');
    this.toleranceRulesCollection = collection(db, 'toleranceRules');
    this.workSessionsWeekCollection = collection(db, 'workSessionsWeek');
  }

  // Obtenir les règles de tolérance pour un site
  async getToleranceRules(siteDeTravail) {
    // Si siteDeTravail est un tableau, on prend le premier site
    const siteId = Array.isArray(siteDeTravail) ? siteDeTravail[0] : siteDeTravail;
    
    const q = query(
      this.toleranceRulesCollection,
      where('siteDeTravail', '==', siteId)
    );
    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;
    return snapshot.docs[0].data();
  }

  // Appliquer les règles de tolérance aux horaires
  async applyToleranceRules(sessionId) {
    const session = await this.getSession(sessionId);
    if (!session) return null;

    const rules = await this.getToleranceRules(session.siteDeTravail);
    if (!rules) return session; // Si pas de règles, retourner la session telle quelle

    const startTime = session.startTime.toDate();
    const endTime = session.endTime.toDate();

    // Appliquer les tolérances du matin
    if (rules.toleranceMatin) {
      const expectedStart = new Date(startTime);
      expectedStart.setHours(rules.heureDebut.split(':')[0]);
      expectedStart.setMinutes(rules.heureDebut.split(':')[1]);

      const toleranceStart = new Date(expectedStart);
      toleranceStart.setMinutes(toleranceStart.getMinutes() + rules.toleranceMatin);

      // Si l'arrivée est dans la tolérance, ajuster à l'heure prévue
      if (startTime <= toleranceStart) {
        startTime.setHours(rules.heureDebut.split(':')[0]);
        startTime.setMinutes(rules.heureDebut.split(':')[1]);
      }
    }

    // Appliquer les tolérances du soir
    if (rules.toleranceSoir) {
      const expectedEnd = new Date(endTime);
      expectedEnd.setHours(rules.heureFin.split(':')[0]);
      expectedEnd.setMinutes(rules.heureFin.split(':')[1]);

      const toleranceEnd = new Date(expectedEnd);
      toleranceEnd.setMinutes(toleranceEnd.getMinutes() - rules.toleranceSoir);

      // Si le départ est dans la tolérance, ajuster à l'heure prévue
      if (endTime >= toleranceEnd) {
        endTime.setHours(rules.heureFin.split(':')[0]);
        endTime.setMinutes(rules.heureFin.split(':')[1]);
      }
    }

    // Calculer le nouveau temps de travail avec les horaires ajustés
    const adjustedSessionDuration = endTime.getTime() - startTime.getTime();
    const adjustedWorkTime = adjustedSessionDuration - session.totalPauseTime;

    // Mettre à jour la session avec les temps ajustés
    await updateDoc(doc(db, 'workSessions', sessionId), {
      adjustedStartTime: Timestamp.fromDate(startTime),
      adjustedEndTime: Timestamp.fromDate(endTime),
      adjustedWorkTime,
      originalWorkTime: session.totalWorkTime // Garder une trace du temps original
    });

    return {
      ...session,
      adjustedStartTime: Timestamp.fromDate(startTime),
      adjustedEndTime: Timestamp.fromDate(endTime),
      adjustedWorkTime
    };
  }

  // Utilitaire pour convertir une position { lat, lng } en { latitude, longitude }
  normalizePosition(pos) {
    if (!pos) return null;
    if (pos.latitude !== undefined && pos.longitude !== undefined) return pos;
    if (pos.lat !== undefined && pos.lng !== undefined) return { latitude: pos.lat, longitude: pos.lng, accuracy: pos.accuracy };
    return pos;
  }

  // Démarrer une nouvelle session de travail
  async startWorkSession(employeeData, location, sessionId) {
    try {
      // Récupérer les sites de travail actuels de l'employé
      const sitesSnapshot = await getDocs(query(
        collection(db, 'employees'),
        where('email', '==', employeeData.email)
      ));
      const employeeDoc = sitesSnapshot.docs[0];
      const employeeCurrentData = employeeDoc.data();
      const currentSites = employeeCurrentData.siteDeTravail || [];

      const sessionData = {
        employeeEmail: employeeData.email,
        employeeName: `${employeeCurrentData.prenom} ${employeeCurrentData.nom}`,
        siteDeTravail: currentSites,
        startTime: Timestamp.now(),
        startLocation: this.normalizePosition(location),
        status: 'active',
        totalWorkTime: 0,
        totalPauseTime: 0,
        device: navigator.userAgent,
        lastUpdateTime: serverTimestamp()
      };

      // Utiliser setDoc pour imposer l'ID
      await setDoc(doc(this.workSessionsCollection, sessionId), sessionData);
      return sessionId;
    } catch (error) {
      console.error('Erreur lors du démarrage de la session:', error);
      throw error;
    }
  }

  // La SEULE fonction fiable pour terminer une session (logique refondue)
  async finalizeSession(sessionId, endLocation) {
    try {
      console.log(`[WSS] Début finalisation de la session ${sessionId}`);
      const sessionRef = doc(this.workSessionsCollection, sessionId);
      const sessionDoc = await getDoc(sessionRef);
      
      if (!sessionDoc.exists()) {
        throw new Error(`Session ${sessionId} non trouvée`);
      }
      
      const session = sessionDoc.data();
      const startTime = session.startTime.toDate();
      const endTime = new Date();
      
      // Terminer la pause active si elle existe
      const activeBreak = await this.getActiveBreak(sessionId);
      if (activeBreak) {
        await this.endBreak(sessionId, activeBreak.startTime.toDate(), endTime, endLocation);
      }

      // Récupérer toutes les pauses de la session
      const allBreaksSnapshot = await getDocs(
        query(this.breaksCollection, where('sessionId', '==', sessionId))
      );

      const updatePromises = [];
      
      // CORRECTION : Utiliser le totalPauseTime existant s'il existe déjà
      let totalPauseTimeMs = session.totalPauseTime || 0;
      
      // Seulement recalculer si pas de totalPauseTime existant
      if (!session.totalPauseTime) {
        console.log(`[WSS] Pas de totalPauseTime existant, calcul à partir des pauses`);
        totalPauseTimeMs = 0;
        allBreaksSnapshot.forEach(breakDoc => {
          const breakData = breakDoc.data();
          if (breakData.duration) {
            totalPauseTimeMs += breakData.duration;
          } else if (breakData.startTime && breakData.endTime) {
            // Sécurité si le champ duration n'existe pas
            const start = breakData.startTime.toDate().getTime();
            const end = breakData.endTime.toDate().getTime();
            totalPauseTimeMs += end - start;
          } else if (breakData.status === 'active' && breakData.startTime) {
            // Pause non terminée, on la clôture à maintenant
            const start = breakData.startTime.toDate().getTime();
            const end = endTime.getTime();
            totalPauseTimeMs += end - start;
            // Et on programme sa mise à jour.
            const breakRef = doc(this.breaksCollection, breakDoc.id);
            updatePromises.push(updateDoc(breakRef, {
              endTime: Timestamp.fromDate(endTime),
              endLocation: this.normalizePosition(endLocation),
              status: 'completed',
              duration: end - start
            }));
          }
        });
      } else {
        console.log(`[WSS] Utilisation du totalPauseTime existant: ${totalPauseTimeMs}ms`);
      }

      const finalTotalPauseTimeMs = Math.round(totalPauseTimeMs);
      const totalSessionMs = endTime.getTime() - startTime.getTime();
      const totalWorkTimeMs = Math.max(0, totalSessionMs - finalTotalPauseTimeMs);
      
      // Mettre à jour le document de session principal.
      console.log(`[WSS] Finalisation de la session ${sessionId} avec: Travail=${totalWorkTimeMs}ms, Pause=${finalTotalPauseTimeMs}ms, Session=${totalSessionMs}ms`);
      
      const sessionUpdateData = {
        endTime: Timestamp.fromDate(endTime),
        endLocation: this.normalizePosition(endLocation),
        status: 'completed',
        lastUpdateTime: serverTimestamp(),
        totalPauseTime: finalTotalPauseTimeMs,
        totalSessionTime: totalSessionMs,
        totalWorkTime: totalWorkTimeMs
      };
      
      updatePromises.push(updateDoc(sessionRef, sessionUpdateData));
      
      // Exécuter toutes les mises à jour en une seule fois
      await Promise.all(updatePromises);
      console.log(`[WSS] Session ${sessionId} finalisée avec succès.`);
      
      // Appel de l'ajustement des tolérances après la clôture de session
      await this.applyToleranceRules(sessionId);

      return sessionUpdateData; // Retourner les données mises à jour
    } catch (error) {
      console.error('Erreur lors de la finalisation de la session:', error);
      throw error;
    }
  }

  // Démarrer une pause (CORRIGÉ)
  async startBreak(sessionId, startTime, location) {
    try {
      const breakData = {
        sessionId,
        startTime: Timestamp.fromDate(startTime),
        startLocation: this.normalizePosition(location),
        status: 'active'
      };

      // Mettre à jour la session pour indiquer qu'elle est en pause
      await updateDoc(doc(this.workSessionsCollection, sessionId), { 
        isOnBreak: true,
        lastUpdateTime: serverTimestamp()
      });

      const docRef = await addDoc(this.breaksCollection, breakData);
      return docRef.id;
    } catch (error) {
      console.error('Erreur lors du démarrage de la pause:', error);
      throw error;
    }
  }

  // Terminer une pause (CORRIGÉ)
  async endBreak(sessionId, breakStartTime, endTime, location) {
    try {
      console.log(`[WSS] Début fin de pause pour session: ${sessionId}`);
      const q = query(
        this.breaksCollection,
        where('sessionId', '==', sessionId),
        where('status', '==', 'active'),
        limit(1)
      );

      const querySnapshot = await getDocs(q);
      if (querySnapshot.empty) {
        console.warn("[WSS] Impossible de trouver une pause active à terminer pour la session", sessionId);
        await updateDoc(doc(this.workSessionsCollection, sessionId), { isOnBreak: false, lastUpdateTime: serverTimestamp() });
        return;
      }

      const breakDoc = querySnapshot.docs[0];
      const breakData = breakDoc.data();
      console.log(`[WSS] Mise à jour de la pause ${breakDoc.id} vers 'completed'.`);
      const breakRef = doc(this.breaksCollection, breakDoc.id);

      // Calculer la durée de la pause
      const breakStartTimeMs = breakData.startTime.toDate().getTime();
      const breakEndTimeMs = endTime.getTime();
      const breakDurationMs = breakEndTimeMs - breakStartTimeMs;

      // Mettre à jour la pause
      await updateDoc(breakRef, {
        endTime: Timestamp.fromDate(endTime),
        endLocation: this.normalizePosition(location),
        status: 'completed',
        duration: breakDurationMs
      });
      
      // Mettre à jour la session
      const sessionRef = doc(this.workSessionsCollection, sessionId);
      const sessionDoc = await getDoc(sessionRef);
      const sessionData = sessionDoc.data();
      const currentTotalPauseTime = sessionData.totalPauseTime || 0;
      
      console.log(`[WSS] Mise à jour de la session ${sessionId} (isOnBreak: false, totalPauseTime: ${currentTotalPauseTime + breakDurationMs}ms).`);
      await updateDoc(sessionRef, { 
        isOnBreak: false,
        lastUpdateTime: serverTimestamp(),
        totalPauseTime: currentTotalPauseTime + breakDurationMs
      });

    } catch (error) {
      console.error('Erreur lors de la fin de la pause:', error);
      throw error;
    }
  }

  // Obtenir une session active pour un employé
  async getActiveSession(employeeEmail) {
    try {
      const q = query(
        this.workSessionsCollection,
        where('employeeEmail', '==', employeeEmail),
        where('status', '==', 'active'),
        orderBy('startTime', 'desc'),
        limit(1)
      );

      const snapshot = await getDocs(q);
      if (snapshot.empty) return null;

      const sessionDoc = snapshot.docs[0];
      const sessionData = { id: sessionDoc.id, ...sessionDoc.data() };

      // CORRECTION : Chercher et attacher la pause active à la session.
      // C'est l'étape cruciale qui manquait.
      if (sessionData.isOnBreak) {
        sessionData.activeBreak = await this.getActiveBreak(sessionData.id);
      }

      return sessionData;
    } catch (error) {
      console.error('Erreur lors de la récupération de la session active:', error);
      throw error;
    }
  }

  // Obtenir une session par ID
  async getSession(sessionId) {
    const docRef = doc(this.workSessionsCollection, sessionId);
    const docSnap = await getDoc(docRef);
    return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } : null;
  }

  // Obtenir la pause active d'une session
  async getActiveBreak(sessionId) {
    const breakQuery = query(
      this.breaksCollection,
      where('sessionId', '==', sessionId),
      where('status', '==', 'active'),
      limit(1)
    );
    const breakSnapshot = await getDocs(breakQuery);
    if (breakSnapshot.empty) {
      return null;
    }
    const breakDoc = breakSnapshot.docs[0];
    return { id: breakDoc.id, ...breakDoc.data() };
  }

  // Obtenir toutes les pauses d'une session
  async getSessionBreaks(sessionId) {
    const q = query(
      this.breaksCollection,
      where('sessionId', '==', sessionId)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  }

  // Obtenir l'historique des sessions d'un employé
  async getEmployeeHistory(employeeEmail) {
    const q = query(
      this.workSessionsCollection,
      where('employeeEmail', '==', employeeEmail),
      where('status', '==', 'completed')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  }

  // Écouter les changements d'une session
  onSessionChange(sessionId, callback) {
    if (!sessionId) return () => {};

    const unsubscribe = onSnapshot(doc(this.workSessionsCollection, sessionId), (doc) => {
      if (!doc.exists()) return;
      callback({
        id: doc.id,
        ...doc.data()
      });
    });

    return unsubscribe;
  }

  // Écouter la pause active
  onActiveBreakChange(sessionId, callback) {
    if (!sessionId) return () => {};

    const q = query(
      this.breaksCollection,
      where('sessionId', '==', sessionId),
      where('status', '==', 'active')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      if (snapshot.empty) {
        callback(null);
      } else {
        const breakDoc = snapshot.docs[0];
        callback({
          id: breakDoc.id,
          ...breakDoc.data(),
          startTime: breakDoc.data().startTime
        });
      }
    });

    return unsubscribe;
  }

  // Calcul temps réel sans persistance
  async updateSessionTimes(sessionId) {
    const session = await this.getSession(sessionId);
    if (!session || session.status !== 'active') return null;

    // Vérifier que le startTime est bien défini et n'est pas null
    if (!session.startTime || !session.startTime.toDate) {
      console.log('Attente du timestamp serveur...');
      return {
        totalWorkTime: 0,
        totalPauseTime: 0,
        completedPauseTime: 0,
        isOnBreak: false
      };
    }

    const now = Math.floor(Date.now() / 1000); // En secondes
    const startTimeSeconds = Math.floor(session.startTime.toDate().getTime() / 1000); // En secondes
    
    console.log('Debug temps :', {
      now: new Date(now * 1000).toISOString(),
      startTime: new Date(startTimeSeconds * 1000).toISOString(),
      difference: Math.floor((now - startTimeSeconds) / 60) + ' minutes'
    });

    // Si le startTime est dans le futur (peut arriver avec serverTimestamp),
    // on retourne 0 pour éviter les temps négatifs
    if (startTimeSeconds > now) {
      console.log('StartTime dans le futur !', {
        startTime: new Date(startTimeSeconds * 1000).toISOString(),
        now: new Date(now * 1000).toISOString()
      });
      return {
        totalWorkTime: 0,
        totalPauseTime: 0,
        completedPauseTime: 0,
        isOnBreak: false
      };
    }

    const elapsedTime = now - startTimeSeconds; // En secondes

    // Calcul des pauses terminées
    const allBreaks = await this.getSessionBreaks(sessionId);
    let completedPauseTime = 0;
    
    // Calculer uniquement les pauses terminées
    allBreaks.forEach(breakItem => {
      if (breakItem.status === 'completed' && breakItem.startTime && breakItem.endTime) {
        const startSeconds = Math.floor(breakItem.startTime.toDate().getTime() / 1000);
        const endSeconds = Math.floor(breakItem.endTime.toDate().getTime() / 1000);
        completedPauseTime += endSeconds - startSeconds;
      }
    });

    // Calcul de la pause en cours
    const activeBreaks = allBreaks.filter(b => b.status === 'active');
    let currentPauseTime = 0;
    if (activeBreaks.length > 1) {
      console.warn('Plusieurs pauses actives détectées pour la session', sessionId, activeBreaks);
    }
    const activeBreak = activeBreaks.length > 0 ? activeBreaks[activeBreaks.length - 1] : null;
    if (activeBreak && activeBreak.startTime) {
      const breakStartSeconds = Math.floor(activeBreak.startTime.toDate().getTime() / 1000);
      currentPauseTime = now - breakStartSeconds;
    }

    // Calcul des temps totaux (tout en secondes)
    const totalPauseTime = completedPauseTime + currentPauseTime;
    const totalWorkTime = Math.max(0, elapsedTime - totalPauseTime);

    // Arrondir à la minute la plus proche
    const roundToMinute = (seconds) => {
      const minutes = seconds / 60;
      const remainder = seconds % 60;
      return (remainder < 30 ? Math.floor(minutes) : Math.ceil(minutes)) * 60;
    };

    return {
      totalWorkTime: roundToMinute(totalWorkTime),
      totalPauseTime: roundToMinute(totalPauseTime),
      completedPauseTime: roundToMinute(completedPauseTime),
      isOnBreak: !!activeBreak,
      activeBreak
    };
  }

  // Helper pour formater le temps
  formatTime(seconds) {
    if (!seconds) return "0h 0m";
    const totalMinutes = Math.floor(seconds / 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}h ${minutes}m`;
  }

  // Ajouter cette nouvelle méthode après updateSessionTimes
  async updateSessionLocation(sessionId, location) {
    try {
      const sessionRef = doc(this.workSessionsCollection, sessionId);
      await updateDoc(sessionRef, {
        currentLocation: location,
        lastLocationUpdate: serverTimestamp()
      });
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la position:', error);
    }
  }

  // Créer ou mettre à jour la feuille de temps hebdo pour un salarié
  async createOrUpdateWorkSessionsWeek(employee, weekStart) {
    // weekStart est déjà le lundi (ne pas recalculer)
    const weekStartDate = parseISODate(weekStart);
    const weekEndDate = getSunday(weekStartDate);
    console.log('[DEBUG] Création feuille de temps pour:', employee.email);
    console.log('[DEBUG] Semaine du:', weekStartDate.toISOString(), 'au', weekEndDate.toISOString());

    // Récupérer toutes les sessions de la semaine
    const sessionsQ = query(
      this.workSessionsCollection,
      where('employeeEmail', '==', employee.email),
      where('startTime', '>=', Timestamp.fromDate(weekStartDate)),
      where('startTime', '<=', Timestamp.fromDate(weekEndDate))
    );
    const sessionsSnap = await getDocs(sessionsQ);
    const sessions = sessionsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    console.log('[DEBUG] Sessions trouvées:', sessions.length);

    // Récupérer toutes les absences de la semaine
    const absencesQ = query(
      collection(db, 'absences'),
      where('employeeEmail', '==', employee.email),
      where('startDate', '<=', formatDateYYYYMMDD(weekEndDate)),
      where('endDate', '>=', formatDateYYYYMMDD(weekStartDate))
    );
    const absencesSnap = await getDocs(absencesQ);
    const absences = absencesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    // Générer l'ID unique (ex: email+date)
    const weekId = `${employee.email}_${formatDateYYYYMMDD(weekStartDate)}`;
    console.log('[DEBUG] weekId généré:', weekId);
    const weekRef = doc(this.workSessionsWeekCollection, weekId);
    const weekDoc = await getDoc(weekRef);
    console.log('[DEBUG] weekDoc.exists:', weekDoc.exists());

    const data = {
      employeeId: employee.id,
      employeeEmail: employee.email,
      employeeName: employee.name || `${employee.prenom || ''} ${employee.nom || ''}`.trim(),
      weekStart: formatDateYYYYMMDD(weekStartDate),
      weekEnd: formatDateYYYYMMDD(weekEndDate),
      sessions,
      absences,
      status: weekDoc.exists() ? weekDoc.data().status : 'En attente de validation Admin',
      debloque: weekDoc.exists() ? weekDoc.data().debloque : false,
      signed: weekDoc.exists() ? weekDoc.data().signed : false,
      adminValidatedAt: weekDoc.exists() ? weekDoc.data().adminValidatedAt : null,
      createdAt: weekDoc.exists() ? weekDoc.data().createdAt : serverTimestamp(),
      updatedAt: serverTimestamp(),
      // Champs pour les notifications de feuilles de temps
      timesheetNotificationCreated: weekDoc.exists() ? weekDoc.data().timesheetNotificationCreated : false,
      timesheetNotificationVu: weekDoc.exists() ? weekDoc.data().timesheetNotificationVu : false,
      timesheetReminderCreated: weekDoc.exists() ? weekDoc.data().timesheetReminderCreated : false,
      reminderCreatedAt: weekDoc.exists() ? weekDoc.data().reminderCreatedAt : null
    };

    // Conserver les champs existants qui ne doivent pas être écrasés
    if (weekDoc.exists()) {
      const existingData = weekDoc.data();
      // Conserver les commentaires et autres champs importants
      if (existingData.employerComment) {
        data.employerComment = existingData.employerComment;
      }
      if (existingData.comment) {
        data.comment = existingData.comment;
      }
      if (existingData.signature) {
        data.signature = existingData.signature;
      }
      if (existingData.pdfUrl) {
        data.pdfUrl = existingData.pdfUrl;
      }
      if (existingData.signedAt) {
        data.signedAt = existingData.signedAt;
      }
      if (existingData.pdfFileName) {
        data.pdfFileName = existingData.pdfFileName;
      }
      // Conserver les champs de notification existants
      if (existingData.timesheetNotificationCreated !== undefined) {
        data.timesheetNotificationCreated = existingData.timesheetNotificationCreated;
      }
      if (existingData.timesheetNotificationVu !== undefined) {
        data.timesheetNotificationVu = existingData.timesheetNotificationVu;
      }
      if (existingData.timesheetReminderCreated !== undefined) {
        data.timesheetReminderCreated = existingData.timesheetReminderCreated;
      }
      if (existingData.reminderCreatedAt) {
        data.reminderCreatedAt = existingData.reminderCreatedAt;
      }
    }

    await setDoc(weekRef, data);
    return data;
  }
}

const workSessionService = new WorkSessionService();
export { workSessionService }; 